import React, {Fragment} from "react";
import {ActionType} from "../list/list/DataTable";
import {BonusConfig, BonusOffer, BonusOfferListRequest, BonusOfferStatus, ListRequest} from "../../http/protocol";
import {getContainer} from "../../ioc/IOCSetup";
import {EntityDataTable} from "../list/list/EntityDataTable";
import {PagerModel} from "../list/list/PagerModel";
import {ListUtils} from "../list/list/ListUtils";
import {toPathParams} from "../../util/UrlUtils";
import {withRouter} from "react-router";
import {ListView, ListViewProps, ListViewState} from "../ListView";
import {Form, FormListener} from "../form/Form";
import {FormGroup} from "../form/FormGroup";
import {Submit} from "../form/Submit";
import {ColumnType} from "../list/list/ColumnType";
import {SelectInput} from "../form/SelectInput";
import {BonusOfferService} from "../../services/BonusOfferService";
import {FormInput} from "../form/FormInput";
import {Validators} from "../form/Validators";
import {t} from "../../i18n";



interface State extends ListViewState<BonusOfferListRequest> {
    form? : BonusOfferListRequest;
    validationMap? : any;
}
interface Props extends ListViewProps  {

}

export class BonusOfferListView extends ListView<State, BonusOfferListRequest> implements FormListener<BonusOfferListRequest>{

    columns = [
        { property : "id", name : "ID", orderBy : "id"},
        { property : "userId", name : "User Id", type : ColumnType.LINK, getUrl : (item : BonusOffer) => "/user/"+item.userId},
        { property:  "bonusConfig.id" , name : "Config Id", type:  ColumnType.LINK, getUrl : (item : BonusOffer) => { return item.bonusConfig ? "/bonus/config/"+item.bonusConfig.id : item.depositRewardConfigId ? "/bonus/deposit-reward/"+item.depositRewardConfigId : false}, renderer: (_p: string, _n: string, item: BonusOffer) => item.bonusConfig?.id ?? item.depositRewardConfigId},
        { property:  "bonusConfig.name" , name : "Name", renderer: (_p: string, _n: string, item: BonusOffer) => item.bonusConfig?.name ?? (item.depositRewardType ? "Deposit " + item.depositRewardType : "")},
        { property: "expires", name : "Expires", type : ColumnType.DATE},
        { property: "status", name : "Status"}
    ];

    actions = [
        { id : "view", name : "Edit", className : "btn btn-primary", iconClassName : "fa fa-edit", type : ActionType.LINK, getLink : (item : any) => "/bonus/offer/" + item.id, filter : (item : BonusOffer) => item.status !== BonusOfferStatus.CLAIMED },
        { id : "delete", name : "Delete", className : "btn btn-danger", iconClassName : "fa fa-trash", type : ActionType.CONFIRMATION, filter : (item : BonusOffer) => item.status !== BonusOfferStatus.CLAIMED}
    ];

    service : BonusOfferService;
    tableRef = React.createRef<EntityDataTable>();
    // The table auto-searches on mount only when a filter is present (see disableInitialSearch below);
    // track whether any search has run so an explicit Search click forces the skipped one exactly once.
    hasSearched = false;

    constructor(props : Props){
        super(props, "/bonus/offers/list");
        this.state = {...this.state, validationMap : {}, form : {...this.state.listRequest} as any};
        this.service = getContainer().getBonusOfferService();
        this.hasSearched = !!(this.state.listRequest?.userId || this.state.listRequest?.status);
    }

    onSubmit() : void | Promise<any> {
        if(this.state.form) {
            // Copy: after paging the form is the same object as listRequest, so resetting the offset in place
            // would hide the change from the comparisons below and the search would never run.
            let form = {...this.state.form, offset : 0};
            // A changed form re-searches via EntityDataTable.componentDidUpdate; an unchanged form does not.
            // Force the search once so the first deliberate search goes through on a page whose initial
            // auto-search was skipped, but ignore repeat clicks that would just re-run the identical query.
            const changed = toPathParams(form) !== toPathParams(this.state.listRequest);
            this.updateListState(form);
            if(!changed && !this.hasSearched) {
                this.tableRef.current?.manualSearch();
            }
            this.hasSearched = true;
        }
    }
    
    onSubmitError() :  void {

    }

    formDidUpdate(formModel: ListRequest, validationMap?: any): void {
        this.setState({form : formModel, validationMap : validationMap});
    }

    onAction(item : BonusConfig, action : string) : Promise<any> | undefined {
        if(action == "delete") {
            return  this.service.remove(""+item.id);
        }
    }

    pagerChanged(pager : PagerModel) {
        if(this.state.listRequest) {
            console.log("pager changed!", pager);
            let listRequest = ListUtils.toListRequest<any>(this.state.listRequest,pager);
            this.updateListState(listRequest);
        }
    }
    getTitle() {
        return t(`listBonusOffers.pageTitle`);
    }

    renderContent() {
        if(!this.state.listRequest) {
            return <div/>;
        }
        return (
            <Fragment>
                <div className={"card-box"}>
                    <h4>{t(`title.searchFilters`)}</h4>
                    <Form formListener={this} modelObject={this.state.form} validationMap={this.state.validationMap}>
                        <FormGroup className={"form-group"}>
                            <label>{t(`label.userId`)}</label>
                            <FormInput model={"userId"} className={"form-control"} type={"integer"} validators={[Validators.integer()]}/>
                        </FormGroup>
                        <FormGroup className={"form-group"}>
                            <label>{t(`label.status`)}</label>
                            <SelectInput optional={true} model={"status"} className={"form-control"} values={Object.keys(BonusOfferStatus)}/>
                            <span className={"help-text"}>{t(`helpText.searchBonusWithStatus`)}</span>
                        </FormGroup>
                        <Submit className={"btn btn-primary"}>{t(`button.search`)}</Submit>
                    </Form>
                </div>
                <EntityDataTable service={this.service}
                                 ref={this.tableRef}
                                 onPagerChange={this.pagerChanged.bind(this)}
                                 columns={this.columns}
                                 listRequest={this.state.listRequest}
                                 onAction={this.onAction.bind(this)}
                                 actions={this.actions}
                                 disableInitialSearch={!this.state.listRequest?.userId && !this.state.listRequest?.status}
                                 notLoadedHint={t(`listBonusOffers.not-loaded-hint`, undefined, "Results are not loaded automatically. Click Search to show bonus offers — filtering by User Id or Status is faster.")}
                                 disableCount={true}/>
            </Fragment>
        );
    }



}

export default withRouter(BonusOfferListView);
